<template>
  <div class="main-content" style="min-height: 766px">
    <section class="section">
      <div class="section-header">
        <h1>Exposure Focus</h1>
      </div>

      <div class="section-body" v-if="role == 'admin' || role == 'operator'">
        <div class="row">
          <div class="col-sm-4">
            <div class="card card-statistic-1">
              <div class="card-icon bg-info">
                <font-awesome-icon icon="user" color="white" />
              </div>
              <div class="card-wrap">
                <div class="card-header">
                  <h4>Total Client NVT</h4>
                </div>
                <div class="card-body">
                  {{ totalNVT | numberFormat }}
                </div>
              </div>
            </div>
          </div>

          <div class="col-sm-4">
            <div class="card card-statistic-1">
              <div class="card-icon bg-info">
                <font-awesome-icon icon="user" color="white" />
              </div>
              <div class="card-wrap">
                <div class="card-header">
                  <h4>Total Hedge NVT</h4>
                </div>
                <div class="card-body">
                  {{ nvt.hedge | numberFormat }}
                </div>
              </div>
            </div>
          </div>

          <div class="col-sm-4">
            <div class="card card-statistic-1">
              <div class="card-icon bg-info">
                <font-awesome-icon icon="user" color="white" />
              </div>
              <div class="card-wrap">
                <div class="card-header">
                  <h4>Total Net NVT</h4>
                </div>
                <div class="card-body">
                  {{ (nvt.hedge - totalNVT) | numberFormat }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-sm-4">
            <div class="card card-statistic-1">
              <div class="card-icon bg-primary">
                <font-awesome-icon icon="user" color="white" />
              </div>
              <div class="card-wrap">
                <div class="card-header">
                  <h4>Total Client Position</h4>
                </div>
                <div class="card-body">
                  {{ totalClientPosition | fixDouble }}
                </div>
              </div>
            </div>
          </div>
          <div class="col-sm-4">
            <div class="card card-statistic-1">
              <div class="card-icon bg-primary">
                <font-awesome-icon icon="user" color="white" />
              </div>
              <div class="card-wrap">
                <div class="card-header">
                  <h4>Total Hedge Position</h4>
                </div>
                <div class="card-body">
                  {{ totalHedgePosition | numberFormat }}
                </div>
              </div>
            </div>
          </div>
          <div class="col-sm-4">
            <div class="card card-statistic-1">
              <div class="card-icon bg-primary">
                <font-awesome-icon icon="user" color="white" />
              </div>
              <div class="card-wrap">
                <div class="card-header">
                  <h4>Total Net Position</h4>
                </div>
                <div class="card-body">
                  {{
                    (totalHedgePosition - totalClientPosition) | numberFormat
                  }}
                </div>
              </div>
            </div>
          </div>
          <div class="col-sm-4">
            <div class="card card-statistic-1">
              <div class="card-icon bg-danger">
                <font-awesome-icon icon="user" color="white" />
              </div>
              <div class="card-wrap">
                <div class="card-header">
                  <h4>Total Client PL</h4>
                </div>
                <div class="card-body">
                  {{ totalClientPl | reverseValue | numberFormat }}
                </div>
              </div>
            </div>
          </div>
          <div class="col-sm-4">
            <div class="card card-statistic-1">
              <div class="card-icon bg-danger">
                <font-awesome-icon icon="user" color="white" />
              </div>
              <div class="card-wrap">
                <div class="card-header">
                  <h4>Total Hedge PL</h4>
                </div>
                <div class="card-body">{{ totalHedgePl | numberFormat }}</div>
              </div>
            </div>
          </div>
          <div class="col-sm-4">
            <div class="card card-statistic-1">
              <div class="card-icon bg-danger">
                <font-awesome-icon icon="user" color="white" />
              </div>
              <div class="card-wrap">
                <div class="card-header">
                  <h4>Total Net PL</h4>
                </div>
                <div class="card-body">
                  {{ totalNetPl | reverseValue | numberFormat }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="section-body">
        <div class="card">
          <div class="card-header">
            <h4>Open Position Table</h4>
            <div class="card-header-action">
              <b-form-select
                v-model="tableView"
                :options="optionsSel.display_format"
                @change="switchTable"
              ></b-form-select>
            </div>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-sm-12 mb-2">
                <b-input-group size="sm">
                  <b-form-input
                    v-model="filter"
                    type="search"
                    id="filterInput"
                    placeholder="Type to Search"
                  ></b-form-input>
                </b-input-group>
              </div>

              <div class="col-sm-12 mb-2">
                <b-input-group size="sm">
                  <multiselect
                    v-model="logins"
                    :options="options"
                    multiple
                    @select="loginInputed"
                    @remove="loginRemoved"
                    placeholder="Select account to watch"
                  ></multiselect>
                </b-input-group>
              </div>
            </div>
            <p>{{ status }}</p>
            <b-table
              class="tb-exposure"
              :sticky-header="true"
              :no-border-collapse="true"
              striped
              hover
              :filter="filter"
              bordered
              responsive
              :busy.sync="isBusy"
              :items="items"
              :fields="fields"
            >
              <!-- A virtual composite column -->
              <template v-slot:cell(login)="data">
                <a
                  href="#"
                  @click="displayBalance(data.item.login, data, $event)"
                  >{{ data.item.login }}</a
                >
              </template>
              <template v-slot:cell(client_profit)="data">
                <span>{{ data.item.client_profit | numberFormat }}</span>
              </template>

              <template v-slot:cell(hedge_profit)="data">
                <span>{{ data.item.hedge_profit | numberFormat }}</span>
              </template>

              <template v-slot:cell(client_total)="data">
                <span>{{ data.item.client_total | numberFormat }}</span>
              </template>

              <template v-slot:cell(hedge_total)="data">
                <span>{{ data.item.hedge_total | numberFormat }}</span>
              </template>

              <template v-slot:cell(total_net_pl)="data">
                <span>{{ data.item.total_net_pl | numberFormat }}</span>
              </template>

              <template v-slot:cell(net_position)="data">
                <span>{{ data.item.net_position | numberFormat }}</span>
              </template>

              <template v-slot:cell(client_settled)="data">
                <span>{{ data.item.client_settled | numberFormat }}</span>
              </template>

              <template v-slot:cell(hedge_settled)="data">
                <span>{{ data.item.hedge_settled | numberFormat }}</span>
              </template>

              <template v-slot:cell(total_client_pl)="data">
                <span>{{ data.item.total_client_pl | numberFormat }}</span>
              </template>

              <template v-slot:cell(client_float)="data">
                <span>{{ data.item.client_float | numberFormat }}</span>
              </template>

              <template v-slot:cell(hedge_float)="data">
                <span>{{ data.item.hedge_float | numberFormat }}</span>
              </template>

              <template v-slot:cell(lifetime_client_pl)="data">
                <span>{{ data.item.lifetime_client_pl | numberFormat }}</span>
              </template>

              <template v-slot:cell(total_hedge_pl)="data">
                <span>{{ data.item.total_hedge_pl | numberFormat }}</span>
              </template>

              <template v-slot:cell(margin_call)="data">
                <span>{{ data.item.margin_call | fixedOne }}%</span>
              </template>

              <template v-slot:cell(fpl)="data">
                <span>{{ data.item.fpl | numberFormat }}%</span>
              </template>
            </b-table>

          </div>
          <div class="card-footer bg-whitesmoke"></div>
        </div>
      </div>
    </section>

    <!-- Start Modal -->
    <b-modal
      ref="my-modal"
      size="xl"
      title="Account Information"
      @ok="handleOk"
    >
      <div class="row">
        <div class="col-sm-6">
          <div class="form-group" v-if="role == 'admin' || role == 'operator'">
            <label class="control-label">Retention Name</label>
            <input
              class="form-control"
              v-model.lazy="modal.retention_name"
              placeholder="Type retention name here"
            />
          </div>
          <div class="form-group">
            <label class="control-label">Balance</label>
            <input class="form-control" v-model="modal.balance" disabled />
          </div>
          <div class="form-group">
            <label class="control-label">Equity</label>
            <input class="form-control" v-model="modal.equity" disabled />
          </div>
          <div class="form-group">
            <label class="control-label">Credit</label>
            <input class="form-control" v-model="modal.credit" disabled />
          </div>
          <div class="form-group">
            <label class="control-label">Client NVT</label>
            <input class="form-control" v-model="modal.total_nvt" disabled />
          </div>
        </div>
        <div class="col-sm-6">
          <div class="form-group">
            <label class="control-label">Client Position</label>
            <input
              class="form-control"
              v-model="modal.client_position"
              disabled
            />
          </div>
          <div class="form-group">
            <label class="control-label">Hedge Position</label>
            <input
              class="form-control"
              v-model="modal.hedge_position"
              disabled
            />
          </div>
          <div class="form-group">
            <label class="control-label">Hedge Position</label>
            <input class="form-control" v-model="modal.net_position" disabled />
          </div>
          <div class="form-group">
            <label class="control-label">Open Ticket/Deal</label>
            <input class="form-control" v-model="modal.total_open" disabled />
          </div>
          <div class="form-group">
            <label class="control-label">Closed Ticket/Deal</label>
            <input class="form-control" v-model="modal.total_close" disabled />
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'

import { getOpenPositionv2, getBalanceInformation, getUsers, getWatchList, addWatchList, removeWatchList, getNvtHedge } from "@/api/trade";
import { mapGetters } from 'vuex';

export default {
  components: { Multiselect },
  data() {
    return {
      fields: [
        { key: "login", stickyColumn: true, sortable: true, label: 'Login' },
        { key: "retention_name", sortable: true, label: 'Retention Name' },
        { key: "fpl", sortable: true, label: 'FPL/B%' },
        { key: "lifetime_client_pl", sortable: true, label: 'Lifetime Client PL' },
        { key: "total_client_pl", sortable: true, label: 'Total Client PL' },
        { key: "client_float", sortable: true, label: 'Client Float' },
        { key: "client_settled", sortable: true, label: 'Client Settled' },
        { key: "total_hedge_pl", sortable: true, label: 'Total Hedge PL' },
        { key: "hedge_float", sortable: true, label: 'Hedge Float' },
        { key: "hedge_settled", sortable: true, label: 'Hedge Settled' }
      ],
      isBusy: false,
      items: [],
      filter: null,
      tableView: "login",
      interval: null,
      logins: [],
      options: ['list', 'of', 'options'],
      optionsSel: {
        display_format: [
          'login', 'both'
        ]
      },
      modal: {
        login: null,
        balance: null,
        equity: null,
        client_position: null,
        hedge_position: null,
        net_position: null,
        retention_name: null,
        index: 0,
        total_nvt: 0,
        total_open: 0,
        total_close: 0,
        credit: 0
      },
      nvt: {
        hedge: 0
      }
    };
  },
  computed: {
    ...mapGetters(['role']),
    rows() {
      return this.items.length;
    },
    swText() {
      return this.tableView == "login"
        ? "Switch to Symbol Table"
        : "Switch to Login Table";
    },
    totalClientPosition() {
      let total = 0;

      this.items.forEach(item => {
        total += item.client_position_plus;
      });

      return total;
    },
    totalNVT() {
      let total = 0;

      this.items.forEach(item => {
        total += item.total_nvt;
      });

      return total;
    },
    totalHedgePosition() {
      let total = 0;

      this.items.forEach(item => {
        total += item.hedge_position_plus;
      });

      return total;
    },
    totalNetPosition() {
      let total = 0;

      this.items.forEach(item => {
        total += item.net_position;
      });

      return total;
    },
    totalClientPl() {
      let total = 0;

      this.items.forEach(item => {
        total += item.total_client_pl;
      });

      return total;
    },
    totalHedgePl() {
      let total = 0;

      this.items.forEach(item => {
        total += Number(item.total_hedge_pl);
      });

      return total;
    },
    totalNetPl() {
      let total = 0;

      this.items.forEach(item => {
        total += Number(item.total_net_pl);
      });

      return total;
    },
    status() {
      if (this.tableView == "login") {
        return `You Watched ${this.items.length} Unique Logins`
      }else if(this.tableView == "symbol") {
        return `You Watched ${this.items.length} Unique Symbols`
      }else{
        return `You Watched ${this.items.length} Login-Symbol Pairs`
      }
    }
  },
  async mounted() {
    await getUsers().then(response => this.options = response.data.data)

    getNvtHedge().then(response => {
      this.nvt.hedge = response.data.data.total;
    })

    // Get Watch List
    await getWatchList().then(response => this.logins = response.data.data.watchs);

    await this.fetchData();

    this.interval = setInterval(() => {
      this.fetchData();
    }, 10000);
  },
  beforeDestroy() {
    clearInterval(this.interval);
  },
  destroyed() { },
  filters: {
    fixedOne(value) {
      return value.toFixed(1)
    }
  },
  methods: {
    handleOk() {

      // Update Retention name
      updateUser(this.modal.login, {
        retention_name: this.modal.retention_name
      }).then(() => {
        // Close the modal
        this.items[this.modal.index].retention_name = this.modal.retention_name
        this.$refs['my-modal'].close();
      })
    },
    loginInputed(login) {
      addWatchList({
        login
      }).then(() => this.fetchData())
    },
    loginRemoved(login) {

      removeWatchList(login).then(() => this.fetchData())
    },
    fetchData() {

      getOpenPositionv2({
        groupBy: this.tableView,
        logins: this.logins
      })
        .then(data => {
          this.items = data.data.trades;
        })
        .catch(() => []);
    },
    displayBalance(login, data, event) {
      this.$refs['my-modal'].toggle('#toggle-btn')
      if (event) {
        event.preventDefault()
      }

      getBalanceInformation(login).then(response => {
        this.modal = {
          login: data.item.login,
          balance: this.fomatter(response.data.user.balance),
          equity: this.fomatter(response.data.user.equity),
          client_position: this.fomatter(data.item.client_position),
          hedge_position: this.fomatter(data.item.hedge_position),
          net_position: this.fomatter(data.item.net_position),
          retention_name: data.item.retention_name,
          index: data.index,
          total_nvt: this.fomatter(data.item.total_nvt),
          total_open: this.fomatter(response.data.meta.open),
          total_close: this.fomatter(response.data.meta.close),
          credit: response.data.user.credit
        };

      })
    },

    fomatter(value) {
      var formatter = new Intl.NumberFormat("en-US", {

      });

      return formatter.format(value);
    },
    switchTable() {
      this.items = [];

      if (this.tableView == "login") {
        this.fields = [
          { key: "login", stickyColumn: true, sortable: true, label: 'Login' },
          { key: "retention_name", sortable: true, label: 'Retention Name' },
          { key: "fpl", sortable: true, label: 'FPL/B%' },
          { key: "lifetime_client_pl", sortable: true, label: 'Lifetime Client PL' },
          { key: "total_client_pl", sortable: true, label: 'Total Client PL' },
          { key: "client_float", sortable: true, label: 'Client Float' },
          { key: "client_settled", sortable: true, label: 'Client Settled' },
          { key: "total_hedge_pl", sortable: true, label: 'Total Hedge PL' },
          { key: "hedge_float", sortable: true, label: 'Hedge Float' },
          { key: "hedge_settled", sortable: true, label: 'Hedge Settled' }
        ];
      } else {
        // Both
        this.fields = [
          { key: "login", stickyColumn: true, sortable: true, label: 'Login' },
          { key: "retention_name", sortable: true, label: 'Retention Name' },
          { key: "symbol", sortable: true, label: 'Symbol' },
          { key: "margin_call", sortable: true, label: 'Margin Call' },
          { key: "total_client_pl", sortable: true, label: 'Total Client PL' },
          { key: "client_float", sortable: true, label: 'Client Float' },
          { key: "client_settled", sortable: true, label: 'Client Settled' },
          { key: "total_hedge_pl", sortable: true, label: 'Total Hedge PL' },
          { key: "hedge_float", sortable: true, label: 'Hedge Float' },
          { key: "hedge_settled", sortable: true, label: 'Hedge Settled' },
          { key: "total_net_pl", sortable: true, label: 'Total Net PL' },
        ]
        this.fetchData();
      }
    },
  }
};
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style scoped>
.tb-exposure {
  max-height: 500px;
}
</style>
